import * as React from "react"
import trans from "trans"
import FileInputComponent from 'react-file-input-previews-base64'

import { css, GridCell, Theme, Grid, Icon, Spinner } from "@bdfx/mergado-ui-kit"

import store from "store"
import { createToast } from "actions"
import toastFactory, { ResponseErrorType } from "app/helpers/toast_factory"
import { GENERAL_ERROR, UPLOAD_IMAGE_FILE } from "template-edit/reducers"
import { URL } from "app/config/api-config"
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_KB, MAX_UPLOAD_FILE_PX } from "./helpers"
import { downloadAllCustomImageSetFiles } from "app/helpers/api"
import { CustomImageSetFile } from "app/types/model-types"
import { AxiosResponse } from "axios"

interface Props {
    allCustomSetFiles: CustomImageSetFile[]
    onFileReady: (file: any, img: HTMLImageElement, isLast: boolean) => void
    onBatchStart?: () => void
}

interface State {
    isDownloading: boolean
}

class CustomSetToolbar extends React.Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isDownloading: false,
        }
    }

    downloadAllImages = () => {
        const { allCustomSetFiles } = this.props
        if (!allCustomSetFiles || !allCustomSetFiles.length || this.state.isDownloading) {
            return
        }

        this.setState({ isDownloading: true })
        downloadAllCustomImageSetFiles()
            .then((response: AxiosResponse<{ zipname: string }>) => {
                const link = document.createElement('a')
                link.href = `${URL.laravelBase}exports/${response.data.zipname}`
                link.download = response.data.zipname
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            })
            .catch(error => {
                console.error('cannot download custom set images', error)
                store.dispatch(createToast(toastFactory(GENERAL_ERROR, error)))
            })
            .finally(() => this.setState({ isDownloading: false }))
    }

    handleFiles = (files) => {
        const { onFileReady, onBatchStart } = this.props

        if (Array.isArray(files)) {
            if (onBatchStart) {
                onBatchStart()
            }

            files.forEach((file, i) => {
                const isLast = i === (files.length - 1)

                if (file.size > MAX_FILE_SIZE_KB) {
                    const error: ResponseErrorType = {
                        message: 'upload_error_large_file',
                        response: null
                    }
                    store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                } else if (ALLOWED_FILE_TYPES.indexOf(file.type) === -1) {
                    const error: ResponseErrorType = {
                        message: 'upload_error_unsupported_type',
                        response: null
                    }
                    store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                } else {
                    const img = new Image()
                    img.src = file.base64

                    img.onload = () => {
                        if (img.width * img.height > MAX_UPLOAD_FILE_PX) {
                            const error: ResponseErrorType = {
                                message: 'upload_error_image_too_big',
                                response: null
                            }
                            store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                        } else {
                            onFileReady(file, img, isLast)
                        }
                    }
                }
            })
        }
    }

    render() {
        const { isDownloading } = this.state
        const iconSize = 38

        return (
            <StyledGrid cols={'auto max-content max-content auto'} gap={'30px'}>
                <GridCell />
                <FileInputComponent
                    key={'set_file_input'}
                    id={'set_file_input'}
                    parentStyle={{ marginTop: 0, display: "inline-block" }}
                    labelStyle={{ fontSize: 14 }}
                    labelText={''}
                    imagePreview={false}
                    multiple={true}
                    buttonComponent={
                        <GridCell className="round-bg clickable">
                            <div><Icon type={'upload-radix'} size={iconSize} color={Theme.blue} /></div>
                            <div className="button-text unselectable">{trans('upload_images')}</div>
                        </GridCell>
                    }
                    callbackFunction={this.handleFiles}
                    accept="image/*"
                />
                <GridCell className="round-bg clickable" onClick={() => this.downloadAllImages()}>
                    <div>{isDownloading ? <Spinner type={'bubbles'} /> : <Icon type={'download-radix'} size={iconSize} color={Theme.blue} />}</div>
                    <div className="button-text unselectable">{trans('download_images')}</div>
                </GridCell>
                <GridCell />
            </StyledGrid>
        )
    }
}

export const StyledGrid = css(Grid)({
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' .round-bg': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(145deg, #ffffff 0%, #eef7fb 100%)',
        borderRadius: '50%',
        width: '90px',
        height: '90px',
        padding: '6px',
        margin: '0 auto',
        border: '1px solid #d7ecf6',
        boxShadow: '0 6px 16px rgba(0, 155, 169, .12), 0 1px 3px rgba(0, 0, 0, .06)',
        ":hover": {
            background: 'linear-gradient(145deg, #eef7fb 0%, #d9edf7 100%)',
            boxShadow: '0 10px 22px rgba(0, 155, 169, .22), 0 2px 4px rgba(0, 0, 0, .08)',
            transition: 'background 3s ease',
        },
    },
    ' .button-text': {
        color: Theme.blue,
        fontSize: '11px',
        lineHeight: '11px',
        fontWeight: 600,
        letterSpacing: '.03em',
        textWrap: 'auto',
        textTransform: 'uppercase',
        marginTop: '5px',
    }
})

export default CustomSetToolbar
